import { uniq, uniqBy } from 'lodash';
import { computed, Ref } from 'vue';
import { Permission, PermissionCategory } from '@infuse/iam/permission/domain';
import { AccessType } from '@infuse/api/iam/permission';

export const usePermissionsCategories = (permissions: Ref<Permission[]>) => {
  const categories = computed(() => {
    return uniqBy(
      permissions.value.map((permission) => permission.category),
      (c) => c.id
    );
  });

  function getPermissionsOfCategory(categoryId: string) {
    return permissions.value.filter((permission) => permission.category.id === categoryId);
  }

  function getObjectTypeWithActionsForCategory(categoryId: string) {
    return uniq(
      getPermissionsOfCategory(categoryId)
        .filter((permission) => permission.accessType === AccessType.ACTION)
        .map((permission) => permission.objectType)
    );
  }

  function getObjectTypeWithCrudForCategory(categoryId: string) {
    return uniq(
      getPermissionsOfCategory(categoryId)
        .filter((permission) => permission.accessType !== AccessType.ACTION)
        .map((permission) => permission.objectType)
    );
  }

  function getActionsOfObjectTypeAndCategory(objectType: string, categoryId: string) {
    return getPermissionsOfCategory(categoryId).filter(
      (permission) => permission.objectType === objectType && permission.accessType === AccessType.ACTION
    );
  }

  function getCrudOfObjectTypeAndCategory(objectType: string, categoryId: string) {
    return getPermissionsOfCategory(categoryId).filter(
      (permission) => permission.objectType === objectType && permission.accessType !== AccessType.ACTION
    );
  }

  const permissionsPerCategory = computed<
    {
      category: PermissionCategory;
      objectTypeActions: {
        objectType: string;
        actions: Permission[];
      }[];
      objectTypeCrud: {
        objectType: string;
        crud: Permission[];
      }[];
    }[]
  >(() => {
    return categories.value.map((category) => ({
      category,
      objectTypeActions: getObjectTypeWithActionsForCategory(category.id).map((objectType) => ({
        objectType,
        actions: getActionsOfObjectTypeAndCategory(objectType, category.id)
      })),
      objectTypeCrud: getObjectTypeWithCrudForCategory(category.id).map((objectType) => ({
        objectType,
        crud: getCrudOfObjectTypeAndCategory(objectType, category.id)
      }))
    }));
  });

  return { categories, getPermissionsOfCategory, permissionsPerCategory };
};
